import * as React from "react"
import { cn } from "@/lib/utils"

interface FormSectionProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  title?: React.ReactNode
  description?: React.ReactNode
  icon?: React.ReactNode
  /** Display action elements on the right side of the section title row. */
  actions?: React.ReactNode
  /** Visually mute the section header — useful for purely-grouping sections. */
  ghost?: boolean
}

/**
 * FormSection groups related form fields under a clear visual header so long
 * dialogs (cases, clients, honoraires) stop reading as one flat list of inputs.
 *
 * Use together with FormSectionGrid to lay out the fields in a responsive grid.
 *
 *   <FormSection title="General info" description="...">
 *     <FormSectionGrid cols={2}>
 *       <FormField ... />
 *       <FormField ... />
 *     </FormSectionGrid>
 *   </FormSection>
 */
const FormSection = React.forwardRef<HTMLDivElement, FormSectionProps>(
  ({ title, description, icon, actions, ghost, className, children, ...props }, ref) => (
    <section
      ref={ref}
      className={cn("space-y-4", className)}
      {...props}
    >
      {(title || description || actions) && (
        <header
          className={cn(
            "flex items-start justify-between gap-3",
            !ghost && "border-b border-border/60 pb-3"
          )}
        >
          <div className="flex items-start gap-3 min-w-0">
            {icon && (
              <span
                className={cn(
                  "mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-lg shrink-0",
                  "bg-primary/10 text-primary [&_svg]:size-4"
                )}
              >
                {icon}
              </span>
            )}
            <div className="min-w-0">
              {title && (
                <h3 className="text-sm font-semibold leading-tight text-foreground truncate">
                  {title}
                </h3>
              )}
              {description && (
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                  {description}
                </p>
              )}
            </div>
          </div>
          {actions && <div className="shrink-0">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  )
)
FormSection.displayName = "FormSection"

interface FormSectionGridProps extends React.HTMLAttributes<HTMLDivElement> {
  cols?: 1 | 2 | 3
}

const FormSectionGrid = React.forwardRef<HTMLDivElement, FormSectionGridProps>(
  ({ cols = 2, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "grid gap-4",
        cols === 1 && "grid-cols-1",
        cols === 2 && "grid-cols-1 md:grid-cols-2",
        cols === 3 && "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
        className
      )}
      {...props}
    />
  )
)
FormSectionGrid.displayName = "FormSectionGrid"

/**
 * FormSectionSeparator inserts vertical whitespace and an optional rule between
 * sibling FormSection blocks.
 */
const FormSectionSeparator = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("py-2", className)} aria-hidden {...props} />
)
FormSectionSeparator.displayName = "FormSectionSeparator"

export { FormSection, FormSectionGrid, FormSectionSeparator }
